import z from "zod";

export type ContractSectionMeta = {
  id: string;
  title: string;
  required?: boolean;
};

export type ContractFieldMeta = {
  id: string; // field id, used as key in formValues
  label: string;
  type: "text" | "date" | "checkbox" | "email";
  required?: boolean;
  sectionId?: string; // optional if server provides grouping
};

export type ContractSessionPayload = {
  id: string; // template id/slug
  title: string;
  version: string;
  contentHash: string;
  sections: ContractSectionMeta[];
  fields: ContractFieldMeta[];
};

export type ContractSessionResponse = {
  sessionId: string;
  payload: ContractSessionPayload;
};

export type ContractSignRequest = {
  sessionId: string;
  version: string;
  contentHash: string;
  formValues: Record<string, any>;
  acknowledgements: string[]; // section ids
  signature: { base64Png: string };
  consent: true;
  signerName: string;
  signerEmail: string | null;
  clientTimestamp: string; // ISO
  clientTimezone: string; // IANA
  // Optional linkage to specific sources
  providerId?: string[];
  institutionId?: string[];
  // Optional identification of signer and subject
  signingUserId?: string;
  requestedUserId?: string;
};

export type ContractSignResponse = {
  sessionId: string;
  signedPdfUrl: string;
  status: "signed" | string;
};

export type ContractRequest = {
  sessionId: string;
  status: string;
  signedPdfUrl?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

// New user contract types matching the API response
export const userSignedDocumentSchema = z
  .object({
    id: z.string().optional(),
    path: z.string().optional(),
    signed_url: z.string().nullable().optional(),
    token: z.string().nullable().optional(),
    bucket: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

// Provider summary details attached to a signing (optional)
export const providerSummarySchema = z
  .object({
    name: z.string().nullable().optional(),
    recipientName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    fax: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    deliveryMethods: z.array(z.string()).optional().default([]),
    type: z.string().nullable().optional(),
  })
  .passthrough();

// Lightweight signing shape; include provider summary and timestamps for display
const userSigningSchema = z
  .object({
    id: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    status: z.string().optional(),
    providerSummary: providerSummarySchema.nullable().optional(),
  })
  .passthrough();

export const userContractSchema = z
  .object({
    id: z.string().optional(),
    signing: userSigningSchema,
    signedDocument: userSignedDocumentSchema.nullable().optional(),
    signedPdfUrl: z.string().nullable().optional(),
  })
  .passthrough();

export const userContractsApiResponseSchema = z.union([
  z.array(userContractSchema),
  z
    .object({
      items: z.array(userContractSchema),
    })
    .passthrough(),
]);

export type UserSignedDocument = z.infer<typeof userSignedDocumentSchema>;
export type UserContract = z.infer<typeof userContractSchema>;
export type ProviderSummary = z.infer<typeof providerSummarySchema>;
